import { IVirtualManifest } from '../builders';
import { VirtualManifestOperations } from './VirtualManifestOperations';
import { ManifestServiceFlags } from '../ManifestServiceFlags';
import {
  VirtualElement,
  VirtualElementAPIType,
} from './providers/VirtualElement';
import { ManifestServiceStats } from '../ManifestServiceStats';
import { CacheMethodResponse } from './decorators';
import { MethodCacheStrategy } from './MethodCacheStrategy';
import { IManifestChangeDetection } from '../ManifestChangeDetection';
import { ManifestQueryAPI, ManifestResolverAPI } from '../handlers';
import type { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';

/**
 * Abstract base class for all manifest resolvers
 * Encapsulates common constructor arguments and provides shared functionality
 */
export abstract class AbstractManifestAPI<
  TApi extends ManifestQueryAPI | ManifestResolverAPI,
  TVirtualElementAPIType extends VirtualElementAPIType,
> {
  protected readonly virtualManifestOperations: VirtualManifestOperations;
  protected readonly virtualManifest: IVirtualManifest;

  constructor(
    protected readonly node: VirtualElement<TVirtualElementAPIType>,
    protected readonly api: TApi,
    protected readonly editorPointerAPI: EditorPointerServiceAPI,
    protected readonly manifestChangeDetection: IManifestChangeDetection,
    protected readonly manifestServiceFlags: ManifestServiceFlags,
    protected readonly manifestServiceStats: ManifestServiceStats,
  ) {
    this.virtualManifest = this.node.getVirtualManifest();

    this.virtualManifestOperations = new VirtualManifestOperations(this.node);
  }

  public getEditorPointer() {
    return this.node.getEditorPointer();
  }

  public getExtensionType() {
    return this.node.getExtensionType();
  }

  @CacheMethodResponse({
    cacheStrategy: MethodCacheStrategy.Permanent,
    transformResponseForStatsCollecting: (x: AbstractManifestAPI<any, any>[]) =>
      x.map((child) => child.getOwnProperties()),
  })
  getChildren() {
    return this.node.getChildren().map((child) => {
      return child.getAPI();
    });
  }

  /**
   * for platform stat collector purposes only
   */
  getOwnProperties() {
    return {
      virtualManifest: this.virtualManifest,
    };
  }
}
